/**
 * Flight enrichment: airline name, route (origin → destination),
 * estimated flight duration and an aircraft photo.
 *
 * - Route:  adsbdb.com callsign API (primary, complete, incl. coordinates),
 *           mit adsb.lol routeset (POST) als Fallback. Beide schlüsselfrei.
 * - Photo:  planespotters.net pub API (photo of the actual airframe by hex)
 *
 * Both are fetched lazily on selection and cached for the session.
 * Requests go same-origin through a reverse proxy (Vite in dev, an Amplify
 * status-200 rewrite in prod) so corporate proxies can't strip CORS headers.
 *
 * PROD (Amplify „Rewrites and redirects“, Typ 200-Rewrite) — nötig, damit die
 * Route- und Demo-Bild-Funktion auch live laufen:
 *   /adsbdb/<*>     -> https://api.adsbdb.com/<*>        (Route, primär)
 *   /adsb-lol/<*>   -> https://api.adsb.lol/<*>          (Route-Fallback, schon vorhanden)
 *   /planespotters/<*> -> https://api.planespotters.net/<*>  (echtes Airframe-Foto)
 *   /wikimedia/<*>  -> https://commons.wikimedia.org/<*> (Typ-Demobild-Suche)
 */

const ADSBDB_BASE = '/adsbdb';
const LOL_BASE = '/adsb-lol';
const PS_BASE = '/planespotters';

// ICAO airline prefix → name (common carriers around European airspace)
const AIRLINES = {
  DLH: 'Lufthansa',
  GEC: 'Lufthansa Cargo',
  CLH: 'Lufthansa CityLine',
  VCE: 'Lufthansa City Airlines',
  EWG: 'Eurowings',
  EWE: 'Eurowings Europe',
  OCN: 'Discover Airlines',
  CFG: 'Condor',
  DLA: 'Air Dolomiti',
  SWR: 'Swiss',
  EDW: 'Edelweiss Air',
  AUA: 'Austrian Airlines',
  BEL: 'Brussels Airlines',
  RYR: 'Ryanair',
  RUK: 'Ryanair UK',
  LDA: 'Lauda Europe',
  EZY: 'easyJet',
  EJU: 'easyJet Europe',
  EZS: 'easyJet Switzerland',
  WZZ: 'Wizz Air',
  WMT: 'Wizz Air Malta',
  VLG: 'Vueling',
  IBE: 'Iberia',
  AEA: 'Air Europa',
  TAP: 'TAP Air Portugal',
  AFR: 'Air France',
  HOP: 'Air France Hop',
  TVF: 'Transavia France',
  TRA: 'Transavia',
  KLM: 'KLM',
  BAW: 'British Airways',
  EFW: 'BA Euroflyer',
  VIR: 'Virgin Atlantic',
  SAS: 'SAS',
  FIN: 'Finnair',
  NOZ: 'Norwegian',
  NSZ: 'Norse Atlantic',
  LOT: 'LOT Polish Airlines',
  CSA: 'Czech Airlines',
  AEE: 'Aegean Airlines',
  ITY: 'ITA Airways',
  THY: 'Turkish Airlines',
  PGT: 'Pegasus Airlines',
  SXS: 'SunExpress',
  KKK: 'AnadoluJet',
  ELY: 'El Al',
  UAE: 'Emirates',
  QTR: 'Qatar Airways',
  ETD: 'Etihad Airways',
  SVA: 'Saudia',
  MSR: 'EgyptAir',
  RJA: 'Royal Jordanian',
  UAL: 'United Airlines',
  AAL: 'American Airlines',
  DAL: 'Delta Air Lines',
  ACA: 'Air Canada',
  SIA: 'Singapore Airlines',
  CPA: 'Cathay Pacific',
  ANA: 'All Nippon Airways',
  JAL: 'Japan Airlines',
  KAL: 'Korean Air',
  AAR: 'Asiana Airlines',
  CCA: 'Air China',
  CES: 'China Eastern',
  CSN: 'China Southern',
  ETH: 'Ethiopian Airlines',
  FDX: 'FedEx',
  UPS: 'UPS Airlines',
  BCS: 'DHL (EAT Leipzig)',
  DHK: 'DHL Air UK',
  ABR: 'ASL Airlines Ireland',
  QY: 'DHL',
  TUI: 'TUI Airways',
  TFL: 'TUI fly Netherlands',
  TUB: 'TUI fly Belgium',
  JAF: 'TUI fly Belgium',
  SDR: 'Sundair',
  MAC: 'Air Arabia Maroc',
  RAM: 'Royal Air Maroc',
  CTN: 'Croatia Airlines',
  JBU: 'JetBlue',
  ICE: 'Icelandair',
  PLM: 'PlayAir',
  GAF: 'German Air Force',
  CHX: 'Christoph (Rettungsheli)',
  DRF: 'DRF Luftrettung'
};

/**
 * Resolve the airline name from an ICAO callsign (e.g. "DLH2YT" → Lufthansa).
 * Returns null for GA/registration callsigns.
 */
export function getAirlineName(callsign) {
  const match = /^([A-Z]{3})\d/.exec((callsign || '').trim().toUpperCase());
  return match ? AIRLINES[match[1]] ?? null : null;
}

const routeCache = new Map(); // callsign -> Promise<route|null>

/**
 * Airport im internen Format ({iata, icao, location, name, lat, lon}).
 * Origin/Destination werden so einheitlich an das Info-Panel geliefert,
 * egal aus welcher Quelle sie stammen.
 * @typedef {{iata:?string, icao:?string, location:string, name:string, lat:?number, lon:?number}} Airport
 */

/**
 * Look up origin/destination airports for a callsign.
 *
 * Zwei unabhängige, schlüsselfreie Quellen für Ausfallsicherheit (die alte
 * adsb.lol-Route-API `GET /api/0/route/{callsign}` ist deprecated und liefert
 * inzwischen 302/503 → „Route lädt nicht mehr zuverlässig“):
 *   1) adsbdb.com  — vollständig, inkl. Flughafen-Koordinaten (für ETA/Distanz)
 *   2) adsb.lol    — `POST /api/0/routeset` (der aktuelle, nicht-deprecatete Endpoint)
 *
 * @param {string} callsign
 * @param {number} [lat] aktuelle Position (nur für den adsb.lol-Fallback nötig)
 * @param {number} [lon]
 * @returns {Promise<{origin:Airport, destination:Airport}|null>}
 */
export function fetchRoute(callsign, lat, lon) {
  const key = (callsign || '').trim().toUpperCase();
  if (!key) return Promise.resolve(null);
  if (routeCache.has(key)) return routeCache.get(key);

  let transient = false;
  const promise = (async () => {
    // 1) Primär: adsbdb.com (vollständig, mit Koordinaten, sehr zuverlässig)
    try {
      const r = await fetchRouteAdsbdb(key);
      if (r) return r;
    } catch {
      transient = true;
    }
    // 2) Fallback: adsb.lol routeset (nicht-deprecateter Endpoint)
    try {
      const r = await fetchRouteAdsbLol(key, lat, lon);
      if (r) return r;
    } catch {
      transient = true;
    }
    return null;
  })().then((route) => {
    // Nur ein endgültiges „keine Route bekannt“ cachen. Nach einem transienten
    // Fehler (Netz/5xx) den Eintrag verwerfen, damit ein späterer Poll neu fragt.
    if (!route && transient) routeCache.delete(key);
    return route;
  });

  routeCache.set(key, promise);
  return promise;
}

/** adsbdb.com: GET /v0/callsign/{callsign} → 404 = unbekannt (kein Retry). */
async function fetchRouteAdsbdb(callsign) {
  const res = await fetch(`${ADSBDB_BASE}/v0/callsign/${encodeURIComponent(callsign)}`, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status === 404) return null; // Callsign ohne bekannte Route
  if (!res.ok) throw new Error(`adsbdb ${res.status}`);
  const json = await res.json();
  const fr = json?.response?.flightroute;
  if (!fr?.origin || !fr?.destination) return null;
  return {
    origin: mapAdsbdbAirport(fr.origin),
    destination: mapAdsbdbAirport(fr.destination)
  };
}

/** adsbdb-Flughafen → internes Airport-Format. */
function mapAdsbdbAirport(a) {
  return {
    iata: a.iata_code || null,
    icao: a.icao_code || null,
    location: a.municipality || a.country_name || '',
    name: a.name || '',
    lat: typeof a.latitude === 'number' ? a.latitude : null,
    lon: typeof a.longitude === 'number' ? a.longitude : null
  };
}

/**
 * adsb.lol: POST /api/0/routeset (Batch-Endpoint, hier mit genau einem Plane).
 * Liefert ein Array von Route-Objekten im vrs-standing-data-Format (`_airports`).
 */
async function fetchRouteAdsbLol(callsign, lat, lon) {
  const res = await fetch(`${LOL_BASE}/api/0/routeset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      planes: [{
        callsign,
        lat: typeof lat === 'number' ? lat : 0,
        lng: typeof lon === 'number' ? lon : 0
      }]
    })
  });
  if (!res.ok) throw new Error(`routeset ${res.status}`);
  const arr = await res.json(); // wirft bei leerem Body → wird oben als transient behandelt
  const entry = Array.isArray(arr)
    ? arr.find((e) => (e?.callsign || '').toUpperCase() === callsign) || arr[0]
    : arr;
  const airports = entry?._airports;
  if (entry?.airport_codes === 'unknown' || !Array.isArray(airports) || airports.length < 2) {
    return null;
  }
  return {
    origin: airports[0],
    destination: airports[airports.length - 1]
  };
}

const photoCache = new Map(); // hex -> Promise<photo|null>

/**
 * Fetch a photo of the aircraft (by ICAO hex) from planespotters.net.
 * @returns {Promise<{src, link, photographer}|null>}
 */
export function fetchAircraftPhoto(hex) {
  const key = (hex || '').trim().toLowerCase();
  if (!key || key.startsWith('demo')) return Promise.resolve(null);
  if (photoCache.has(key)) return photoCache.get(key);

  const promise = (async () => {
    const res = await fetch(`${PS_BASE}/pub/photos/hex/${key}`, {
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error(`planespotters ${res.status}`);
    const json = await res.json();
    const photo = json?.photos?.[0];
    if (!photo?.thumbnail_large?.src) return null;
    return {
      src: photo.thumbnail_large.src,
      link: photo.link,
      photographer: photo.photographer
    };
  })().catch(() => {
    photoCache.delete(key);
    return null;
  });

  photoCache.set(key, promise);
  return promise;
}

const WIKI_BASE = '/wikimedia';
const typePhotoCache = new Map(); // Suchbegriff -> Promise<photo|null>

/**
 * Fallback-Foto, wenn kein echtes Foto DIESES Airframes (per Hex) existiert:
 * ein repräsentatives Bild des TYPS aus Wikimedia Commons (schlüsselfrei,
 * lizenzsauber). Wird in der UI klar als „Demo“ gekennzeichnet, damit es nicht
 * mit dem echten Flugzeug verwechselt wird (demo:true).
 * @param {string} term  z. B. der Klarname „Airbus A320“
 * @returns {Promise<{src, link, photographer, demo:true}|null>}
 */
export function fetchTypePhoto(term) {
  const key = (term || '').trim();
  if (!key) return Promise.resolve(null);
  if (typePhotoCache.has(key)) return typePhotoCache.get(key);

  const promise = (async () => {
    const url = `${WIKI_BASE}/w/api.php?action=query&format=json`
      + `&generator=search&gsrsearch=${encodeURIComponent(`${key} aircraft`)}`
      + `&gsrnamespace=6&gsrlimit=1`
      + `&prop=imageinfo&iiprop=url|extmetadata&iiurlwidth=480`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`wikimedia ${res.status}`);
    const json = await res.json();
    const pages = json?.query?.pages;
    if (!pages) return null;
    const info = Object.values(pages)[0]?.imageinfo?.[0];
    const src = info?.thumburl || info?.url;
    if (!src) return null;
    return {
      src,
      link: info?.descriptionurl || src,
      photographer: stripHtml(info?.extmetadata?.Artist?.value) || 'Wikimedia Commons',
      demo: true
    };
  })().catch(() => {
    typePhotoCache.delete(key);
    return null;
  });

  typePhotoCache.set(key, promise);
  return promise;
}

/** HTML aus Wikimedia-Metadaten (z. B. Artist) auf reinen Text reduzieren. */
function stripHtml(s) {
  return (s || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Great-circle distance in km between two points ({lat, lon}).
 */
export function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Estimate total flight duration from the route's great-circle distance.
 * Short hops cruise slower; adds a fixed climb/descent/taxi overhead.
 * @returns {{distanceKm: number, minutes: number}|null}
 */
export function estimateFlightDuration(origin, destination) {
  if (typeof origin?.lat !== 'number' || typeof destination?.lat !== 'number') {
    return null;
  }
  const distanceKm = haversineKm(origin, destination);
  if (distanceKm < 10) return null;
  const cruiseKmh = distanceKm < 800 ? 620 : 820;
  const minutes = Math.round((distanceKm / cruiseKmh) * 60 + 25);
  return { distanceKm: Math.round(distanceKm), minutes };
}

/**
 * Format minutes as "2h 15min" / "45min".
 */
export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}min` : `${m}min`;
}
